import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, List, ListItem, ListItemText, Typography, styled, useMediaQuery, useTheme } from "@mui/material";
import image1 from "../../../../assets/programme/restoringroof.jpeg";
import FooterInnerPagee from "../../../../common/FooterInnerPagee";
import Header from "../../../../common/Header";
import { useNavigate } from "react-router-dom";
import LeftSideNavigation from "../../../../common/LeftSideNavigation";

const ScrollContainer = styled("div")(({ theme }) => ({
  display: "flex",
  overflowX: "auto",
  overflowY: "hidden",
  scrollSnapType: "x mandatory",
  scrollBehavior: "smooth",
  height: "100%",
  width: "100%",
  position: "relative",
  margin: 0,
  padding: 0,
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const ScrollItem = styled("div")({
  flex: "0 0 100%",
  scrollSnapAlign: "start",
  height: "100%",
  position: "relative",
  margin: 0,
  padding: 0,
});

const ScrollImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
  margin: 0,
  padding: 0,
});

const DotsContainer = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: "20px",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  zIndex: 10,
  pointerEvents: "auto",
}));

const Dot = styled("div")(({ active }) => ({
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  cursor: "pointer",
  backgroundColor: active ? "#fff" : "rgba(255,255,255,0.5)",
  transition: "background-color 0.3s ease",
  "&:hover": {
    backgroundColor: "white",
    transform: "scale(1.2)",
  },
}));

const RestoringHope = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const images = [image1];
  const [scrolled, setScrolled] = useState(false);
  const contentRef = useRef(null);

  const handleDotClick = (index) => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollWidth = container.scrollWidth;
    const itemWidth = scrollWidth / images.length;
    container.scrollTo({ left: itemWidth * index, behavior: "smooth" });
    setCurrentIndex(index);
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollLeft = container.scrollLeft;
    const itemWidth = container.offsetWidth;
    const index = Math.round(scrollLeft / itemWidth);
    setCurrentIndex(index);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setScrolled(scrollTop > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const interval = setInterval(() => {
      const nextIndex = (currentIndex + 1) % images.length;
      setCurrentIndex(nextIndex);
      container.scrollTo({
        left: container.clientWidth * nextIndex,
        behavior: "smooth",
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, images.length]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollWidth = container.scrollWidth;
      const itemWidth = scrollWidth / images.length;
      const newIndex = Math.round(container.scrollLeft / itemWidth);
      setCurrentIndex(newIndex);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Header />

      {/* Hero Section - scrolls away normally */}
      <Box
        sx={{
          width: "100%",
          height: isMobile ? "50vh" : "65vh",
          position: "relative",
        }}
      >
        <ScrollContainer ref={scrollRef}>
          {images.map((img, index) => (
            <ScrollItem key={index}>
              <ScrollImage src={img} alt={`Banner ${index + 1}`} />
            </ScrollItem>
          ))}
        </ScrollContainer>

        <DotsContainer>
          {images.map((_, idx) => (
            <Dot
              key={idx}
              active={currentIndex === idx}
              onClick={() => handleDotClick(idx)}
            />
          ))}
        </DotsContainer>
      </Box>

      {/* Main Content Section */}
      <Grid container sx={{
        px: isMobile ? 2 : 4,
        py: isMobile ? 2 : 4,
        position: 'relative',
        maxWidth: '100%',
        margin: '0 auto',
      }}>
        {/* Left Side Navigation - Sticky */}
        {!isMobile && (
          <Grid item md={2} sx={{
            position: 'sticky',
            top: 80,
            height: 'calc(100vh - 64px)',
            alignSelf: 'flex-start',
            overflowY: 'auto',
            zIndex: 1,
            pr: 2,
          }}>
            <LeftSideNavigation />
          </Grid>
        )}

        {/* Right Side Content - Scrollable */}
        <Grid item xs={12} md={10} sx={{
          border: '1px solid #7f6b56',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}>
          <Box
            sx={{
              color: "#7f6b56",
              fontFamily: "Inter, sans-serif",
              px: isMobile ? 2 : 3,
              py: isMobile ? 3 : 6,
              position: "relative",
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif" }}
            >
              The Reef Restoration Project – Lakshadweep
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              fontWeight="600"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif", mt: 2 }}
            >
              Restoring Hope, One Coral at a Time
            </Typography>
            <Typography variant="body1" gutterBottom sx={{
              mt: 2,
              lineHeight: 1.6,
              fontFamily: "Montserrat, sans-serif",
              fontSize: isMobile ? "16px" : "18px",
              textAlign: "left"
            }}>
              The fragile coral reefs of the Lakshadweep Archipelago are a lifeline for island communities—providing food, coastal protection, and livelihoods. However, rising sea temperatures, destructive fishing practices, and climate change have led to severe reef degradation across the atolls. In response to this ecological crisis, REEF launched The Reef Restoration Project—a community-driven initiative aimed at reviving coral reef ecosystems through science-based restoration and sustainable stewardship.
            </Typography>

            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif", mt: 6 }}
            >
              Seagrass Restoration Project – Lakshadweep Archipelago
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              fontWeight="600"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif", mt: 2 }}
            >
              Restoring Life Below Water, One Meadow at a Time
            </Typography>
            <Typography variant="body1" gutterBottom sx={{
              mt: 2,
              lineHeight: 1.6,
              fontFamily: "Montserrat, sans-serif",
              fontSize: isMobile ? "16px" : "18px",
              textAlign: "left"
            }}>
              The Seagrass Restoration Project in Lakshadweep is a pioneering conservation initiative dedicated to restoring and protecting the vital seagrass ecosystems of this unique coral atoll region. Spearheaded by REEF (Research and Environmental Education Foundation), in collaboration with local communities, researchers, and policy advocates, the project aims to revive degraded seagrass meadows, strengthen marine biodiversity, and enhance the climate resilience of the archipelago.
            </Typography>

            <Typography
              variant={isMobile ? "body1" : "h6"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif", mt: 4, textAlign: "left" }}
            >
              Why Seagrass Matters
            </Typography>
            <Typography variant="body1" gutterBottom sx={{
              mt: 1,
              fontFamily: "Montserrat, sans-serif",
              fontSize: isMobile ? "16px" : "18px",
              textAlign: "left"
            }}>
              Often overlooked, seagrass meadows are underwater powerhouses. They:
            </Typography>
            <List
              sx={{
                listStyleType: "disc",
                pl: 4,
                typography: "body1",
                fontFamily: "Montserrat, sans-serif",
                fontSize: isMobile ? "16px" : "18px",
              }}
            >
              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      Provide critical nursery grounds for fish, including commercially important and endangered species.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      Stabilize sediment and protect coastlines from erosion.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      Improve water clarity and support coral health.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      Act as powerful carbon sinks, helping to mitigate climate change.
                    </Typography>
                  }
                />
              </ListItem>
            </List>

            <Typography variant="body1" gutterBottom sx={{
              mt: 2,
              fontFamily: "Montserrat, sans-serif",
              fontSize: isMobile ? "16px" : "18px",
              textAlign: "left"
            }}>
              In the Lakshadweep Archipelago, these habitats are crucial for the livelihoods of local communities dependent on fishing and coastal resources.
            </Typography>

            <Typography
              variant={isMobile ? "body1" : "h6"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif", mt: 4, textAlign: "left" }}
            >
              Our Objectives
            </Typography>
            <List
              sx={{
                listStyleType: "disc",
                pl: 4,
                typography: "body1",
                fontFamily: "Montserrat, sans-serif",
                fontSize: isMobile ? "16px" : "18px",
              }}
            >
              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      <strong>Ecological Restoration:</strong> Restore degraded seagrass meadows through transplantation and natural recruitment methods.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      <strong> Community Engagement:</strong>  Train and involve local youth, fishers, and school students in seagrass monitoring and restoration.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      <strong> Research and Monitoring:</strong> Conduct scientific assessments to understand seagrass distribution, health, and associated biodiversity.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      <strong>Awareness and Advocacy:</strong> Build awareness among policymakers and the public about the ecological and economic value of seagrass ecosystems.
                    </Typography>
                  }
                />
              </ListItem>
            </List>

            <Typography
              variant={isMobile ? "body1" : "h6"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif", mt: 4, textAlign: "left" }}
            >
              Community at the Heart
            </Typography>
            <Typography variant="body1" gutterBottom sx={{
              mt: 2,
              lineHeight: 1.6,
              fontFamily: "Montserrat, sans-serif",
              fontSize: isMobile ? "16px" : "18px",
              textAlign: "left"
            }}>
              Our approach is deeply rooted in the belief that lasting conservation is only possible when local communities are empowered. The project actively trains community volunteers as "Seagrass Guardians" who participate in mapping, transplanting, and long-term monitoring of the meadows. Their traditional knowledge, combined with modern science, forms the backbone of our success.
            </Typography>

            <Typography
              variant={isMobile ? "body1" : "h6"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif", mt: 4, textAlign: "left" }}
            >
              Be Part of the Change
            </Typography>
            <Typography variant="body1" gutterBottom sx={{
              mt: 2,
              lineHeight: 1.6,
              fontFamily: "Montserrat, sans-serif",
              fontSize: isMobile ? "16px" : "18px",
              textAlign: "left"
            }}>
              Join us in restoring Lakshadweep's underwater meadows. Whether you're a student, diver, scientist, policymaker, or concerned citizen—there's a role for you.
              <br />
              <br />
              Together, we can protect this hidden treasure of the Indian Ocean.
            </Typography>

            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif", mt: 6 }}
            >
              Community Awareness Program
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              fontWeight="600"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif", mt: 2 }}
            >
              Empowering Communities, Protecting Oceans
            </Typography>
            <Typography variant="body1" gutterBottom sx={{
              mt: 2,
              fontFamily: "Montserrat, sans-serif",
              fontSize: isMobile ? "16px" : "18px",
              textAlign: "left"
            }}>
              The Lakshadweep Archipelago is a jewel of the Indian Ocean — a fragile ecosystem rich in biodiversity, culture, and traditional knowledge. At the heart of its conservation lies an essential truth: local communities are the first stewards of their environment.
              <br />
              <br />
              Our Community Awareness Program is an initiative led by REEF to build environmental consciousness, promote sustainable practices, and foster stewardship among the islanders of Lakshadweep. We engage with fishermen, youth, women's self-help groups, and students across the atolls to create a future where conservation and community go hand in hand.
            </Typography>

            <List
              sx={{
                listStyleType: "disc",
                pl: 4,
                typography: "body1",
                fontFamily: "Montserrat, sans-serif",
                fontSize: isMobile ? "16px" : "18px",
              }}
            >
              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      Raise awareness about the importance of marine biodiversity, including coral reefs, sea cucumbers, and other vital species.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      Promote sustainable fishing practices that protect both livelihoods and ecosystems.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      Encourage youth participation in marine conservation through workshops, field visits, and citizen science.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      Empower women through skill-building and leadership training in eco-friendly livelihoods.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      Foster climate resilience by sharing knowledge on local environmental challenges and solutions.
                    </Typography>
                  }
                />
              </ListItem>
            </List>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif", mt: 4, textAlign: "left" }}
            >
              Key Activities
            </Typography>
            <List
              sx={{
                listStyleType: "disc",
                pl: 4,
                typography: "body1",
                fontFamily: "Montserrat, sans-serif",
                fontSize: isMobile ? "16px" : "18px",
              }}
            >
              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      <strong>School & College Outreach:</strong> Interactive sessions, reef walks, and eco-clubs that introduce students to marine ecology and conservation.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      <strong>Fisherfolk Engagement:</strong> Workshops on sustainable fishing, reef-safe techniques, and co-management of marine resources.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      <strong>Women-Led Initiatives:</strong> Training in waste management, eco-tourism, and lagoon-friendly livelihoods to strengthen women's roles in conservation.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      <strong>Community Storytelling:</strong> We document and share traditional ecological knowledge and local stories that connect culture to conservation.
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem sx={{ display: "list-item", lineHeight: 1.6, py: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif" }}>
                      <strong>Hands-On Learning:</strong> Beach clean-ups, reef monitoring demonstrations, and citizen science projects open to all age groups.
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>
        </Grid>

        {/* Mobile Navigation - appears after content */}
        {isMobile && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <LeftSideNavigation />
          </Grid>
        )}
      </Grid>

      <FooterInnerPagee />
    </>
  );
};

export default RestoringHope;